import { Button } from '@/components/ui/button';
import { useParams } from 'react-router-dom';
import { LucideDownload } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { downloadFile } from '@/utils/util';
import { useFetchWithAuth } from '@/utils/fetch-auth';

import { FormLayout, FormSection, SectionHeader } from './tracking-details-form/base';
import { FormHeaderSection } from './tracking-details-form/shared-sections';
import { useIntegrationStore } from '../stores/tracking-store';

interface DownloadGtmContainerProps {
  platformName: string;
}

export const DownloadGtmContainerComponent = ({ platformName }: DownloadGtmContainerProps) => {
  const fetchWithAuth = useFetchWithAuth();
  const { toast } = useToast();
  const params = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const { integration } = useIntegrationStore();
  const metadata = integration?.metadata as { tagDelivery?: string | null } | null | undefined;
  const firesDirectly = metadata?.tagDelivery === 'DIRECT';

  const handleDownload = async () => {
    if (!params.id) {
      toast({
        title: 'Invalid Request',
        description: 'Missing integration ID.',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_CONTEXTUAL_API_URL}/integration/gtm-container?integrationId=${params.id}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch GTM container. Status: ${response.status}`);
      }

      const data = (await response.json()) as JSON;
      downloadFile(data, 'contextual-gtm.json');

      toast({
        title: 'Success',
        description: 'GTM container downloaded successfully.',
        variant: 'default'
      });
    } catch (error: any) {
      console.error('Error downloading GTM container:', error);
      toast({
        title: 'Error',
        description: 'Failed to download GTM container.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (firesDirectly) {
    return (
      <FormLayout>
        <FormHeaderSection title="No GTM container needed">
          This {platformName} integration fires its browser pixel directly from Contextual, so there
          is nothing to import into Google Tag Manager. If you still have Contextual&apos;s{' '}
          {platformName} tags in a GTM container of your own, remove or pause them to avoid counting
          events twice. Switch back to Google Tag Manager under Product Settings.
        </FormHeaderSection>
      </FormLayout>
    );
  }

  return (
    <FormLayout>
      <FormHeaderSection title="Download GTM Container">
        This is where you can download the Contextual Google Tag Manager container for your{' '}
        {platformName} integration.
      </FormHeaderSection>
      <FormSection>
        <SectionHeader>1. Download GTM Container</SectionHeader>
        <Button
          className="w-2/5"
          type="submit"
          onClick={() => void handleDownload()}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Downloading...
            </>
          ) : (
            <>
              Contextual GTM Container
              <LucideDownload className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </FormSection>
      <FormSection>
        <SectionHeader>2. Upload the GTM container into Google Tag Manager</SectionHeader>
        <p className="text-sm text-gray-600 mt-2">
          {`In Google Tag Manager, go to Admin > Import Container on the right hand side and upload the GTM container you downloaded.`}
        </p>
      </FormSection>
      <FormSection>
        <SectionHeader>3. Publish your container.</SectionHeader>
        <p className="text-sm text-gray-600 mt-2">
          {`In Google Tag Manager, go to your Container's Overview page and click "Submit" to publish your container.`}
        </p>
      </FormSection>
    </FormLayout>
  );
};

export default DownloadGtmContainerComponent;
